import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Play, Clock, Calendar, Users, ExternalLink, MessageCircle } from 'lucide-react';
import Link from '../components/Link';
import {
  format,
  isAfter,
  isBefore,
  setHours,
  setMinutes,
  setSeconds,
  addDays,
  getDay
} from 'date-fns';
import {
  toZonedTime,
  fromZonedTime
} from 'date-fns-tz';
import { fetchLibraryItems, fetchLibraryItemsWithTemp } from '../api/adminService';
import { addToCalender } from '../api/addToCalender';

const TIMEZONE = 'Africa/Lagos';
interface StudyData {
  id: string;
  title: string;
  description: string;
  youtubeUrl: string;
  thumbnail: string;
  date: string;
  duration: string;
  library_type: string;
  study_notes: {
    visible: boolean;
    content: string;
  };
  speaker: string;
}

type LiveSession = {
  id: string;
  title: string;
  description: string;
  youtubeUrl: string;
  thumbnail: string;
  isLive: boolean;
  scheduledTime: string;
  speaker: string;
};

export const getNextLiveSession = (): LiveSession => {
  const nowUTC = new Date();
  // const nowLagos = utcToZonedTime(nowUTC, TIMEZONE);
  const nowLagos = toZonedTime(new Date(), 'Africa/Lagos');
  const today = getDay(nowLagos);

  const getSessionDate = (date: Date) =>
    setSeconds(setMinutes(setHours(date, 19), 0), 0); // 7:00 PM Lagos

  let sessionDate = getSessionDate(nowLagos);
  const endSession = setHours(setMinutes(setSeconds(sessionDate, 0), 0), 21); // 9:00 PM

  const isLiveDay = today === 1 || today === 4;
  const isLive =
    isLiveDay && isAfter(nowLagos, sessionDate) && isBefore(nowLagos, endSession);

  if (!isLiveDay || isAfter(nowLagos, endSession)) {
    for (let i = 1; i <= 7; i++) {
      const check = addDays(nowLagos, i);
      const day = getDay(check);
      if (day === 1 || day === 4) {
        sessionDate = getSessionDate(check);
        break;
      }
    }
  }

  const scheduledTimeUTC = fromZonedTime(sessionDate, TIMEZONE).toISOString();

  return {
    id: '123',
    title: 'BIBLE STUDY WITH TITOLUWA',
    description: 'Join us for an interactive Bible study exploring how to walk in the authority God has given us.',
    youtubeUrl: 'https://www.youtube.com/@titothewriter/streams',
    thumbnail: 'https://i.ytimg.com/vi/LqIBTgG9M2Q/hqdefault.jpg',
    isLive,
    scheduledTime: scheduledTimeUTC,
    speaker: 'Pst. Titoluwa Olumideko'
  };
};

const handleReminderClick = () => {
  const now = toZonedTime(new Date(), 'Africa/Lagos');
  const today = getDay(now); // Sunday=0, Monday=1, ..., Saturday=6

  // Helper to build event start date
  const getSessionDate = (date: Date) =>
    setSeconds(setMinutes(setHours(date, 19), 0), 0); // 7:00 PM Lagos

  let nextSession: Date | null = null;

  // Check if today is Mon/Thu but already passed
  const isLiveDay = today === 1 || today === 4;
  let candidate = getSessionDate(now);

  if (isLiveDay && isBefore(now, candidate)) {
    // Still upcoming today
    nextSession = candidate;
  } else {
    // Find next Mon/Thu in the week
    for (let i = 1; i <= 7; i++) {
      const check = addDays(now, i);
      const day = getDay(check);
      if (day === 1 || day === 4) {
        nextSession = getSessionDate(check);
        break;
      }
    }
  }

  if (!nextSession) return; // failsafe

  addToCalender({
    title: "BIBLE STUDY WITH TITOLUWA",
    description:
      "Join us for an interactive Bible study exploring how to walk in the authority God has given us.",
    location: "https://www.youtube.com/@titothewriter/streams",
    start: nextSession,
    duration: { hours: 2, minutes: 0 }, // 7PM – 9PM
    fileName: "Bible_Study_Reminder.ics",
    repeat: "custom",
    customRepeat: "RRULE:FREQ=WEEKLY;BYDAY=MO,TH"
  });
};

export default function Live() {
  const [liveSession, setLiveSession] = useState<LiveSession | null>(null);
  const [timeUntilLive, setTimeUntilLive] = useState<string>('');
  const [sampleResources, setSampleResources] = useState<StudyData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const session = getNextLiveSession();
    setLiveSession(session);
    setLoading(false);
  }, []);

  useEffect(() => {
    const loadItems = async () => {
      const finalItems = await fetchLibraryItemsWithTemp();
      setSampleResources(finalItems);
    };

    // loadItems();
  }, []);

  useEffect(() => {
    if (!liveSession || liveSession.isLive) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const scheduledTime = new Date(liveSession.scheduledTime).getTime();
      const difference = scheduledTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        if (days > 0) {
          setTimeUntilLive(`${days}d ${hours}h ${minutes}m`);
        } else if (hours > 0) {
          setTimeUntilLive(`${hours}h ${minutes}m ${seconds}s`);
        } else {
          setTimeUntilLive(`${minutes}m ${seconds}s`);
        }
      } else {
        setTimeUntilLive('Starting soon...');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [liveSession]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!liveSession) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">No Live Session Scheduled</h1>
          <p className="text-gray-400 mb-6">Check back later for upcoming live Bible studies.</p>
          <Link to="/library" className="text-amber-400 hover:text-amber-300">
            Browse Previous Studies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24"
    >
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            {liveSession.isLive ? (
              <div className="inline-flex items-center gap-3 bg-red-600 px-6 py-3 rounded-full">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                <span className="font-bold text-lg">NOW LIVE</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-3 bg-amber-600 px-6 py-3 rounded-full">
                <Clock className="w-5 h-5" />
                <span className="font-bold">Starts in {timeUntilLive}</span>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{liveSession.title}</h1>
            <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto">{liveSession.description}</p>

            <div className="flex items-center justify-center gap-6 text-gray-400 mb-8">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span>{liveSession.speaker}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{new Date(liveSession.scheduledTime).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{new Date(liveSession.scheduledTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-12"
          >
            <div className="relative group max-w-3xl mx-auto">
              <div className="aspect-video rounded-2xl overflow-hidden bg-gray-800 shadow-2xl">
                <img
                  src={sampleResources[0]?.thumbnail || liveSession.thumbnail}
                  alt={sampleResources[0]?.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/60 transition-colors">
                  {/* <p>sample sampleResources {JSON.stringify(sampleResources[0])}</p> */}
                  <motion.a
                    href={liveSession.isLive ? sampleResources[0]?.youtubeUrl : undefined}
                    onClick={(e) => {
                      if (!liveSession.isLive) {
                        e.preventDefault(); // stop default link behavior
                        handleReminderClick();
                      }
                    }}
                    target={liveSession.isLive ? "_blank" : undefined}
                    rel={liveSession.isLive ? "noopener noreferrer" : undefined}
                    className={`flex items-center gap-3 px-8 py-4 rounded-full text-white font-medium transition-colors cursor-pointer ${liveSession.isLive
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-amber-600 hover:bg-amber-700"
                      }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Play className="w-6 h-6" />
                    <span>{liveSession.isLive ? "Join Live Stream" : "Set Reminder"}</span>
                    <ExternalLink className="w-5 h-5" />
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to={`/study?id=${sampleResources[0]?.id}`}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-lg text-white font-medium transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Study Notes & Q&A</span>
            </Link>
            <Link
              to="/library"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Browse Previous Studies →
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-16 text-center"
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 max-w-2xl mx-auto">
              <h3 className="text-xl font-bold mb-4">What to Expect</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Interactive Bible study with live Q&A</li>
                <li>• Practical application of biblical principles</li>
                <li>• Community discussion and prayer</li>
                <li>• Study notes available after the session</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

function Live_() {
  const [liveSession, setLiveSession] = useState<LiveSession | null>(null);
  const [timeUntilLive, setTimeUntilLive] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLiveSession = async () => {
      // Mock data - replace with actual API call
      setTimeout(() => {
        const session: LiveSession = {
          id: '123',
          title: 'BIBLE STUDY WITH TITOLUWA',
          description: 'Join us for an interactive Bible study exploring how to walk in the authority God has given us.',
          youtubeUrl: 'https://www.youtube.com/@titothewriter/streams',
          thumbnail: 'https://i.ytimg.com/vi/9WQnspziHyI/hqdefault.jpg?sqp=-oaymwEjCNACELwBSFryq4qpAxUIARUAAAAAGAElAADIQj0AgKJDeAE=&rs=AOn4CLAoIBgCnMWm5dbkGqHwZsqJPmalBQ',
          isLive: false, // Change to true to test live state
          scheduledTime: '2024-03-20T19:00:00Z',
          speaker: 'Pst. Titoluwa Olumideko'
        };
        setLiveSession(session);
        setLoading(false);
      }, 1000);
    };

    fetchLiveSession();
  }, []);

  useEffect(() => {
    if (!liveSession || liveSession.isLive) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const scheduledTime = new Date(liveSession.scheduledTime).getTime();
      const difference = scheduledTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        if (days > 0) {
          setTimeUntilLive(`${days}d ${hours}h ${minutes}m`);
        } else if (hours > 0) {
          setTimeUntilLive(`${hours}h ${minutes}m ${seconds}s`);
        } else {
          setTimeUntilLive(`${minutes}m ${seconds}s`);
        }
      } else {
        setTimeUntilLive('Starting soon...');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [liveSession]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!liveSession) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">No Live Session Scheduled</h1>
          <p className="text-gray-400 mb-6">Check back later for upcoming live Bible studies.</p>
          <Link to="/library" className="text-amber-400 hover:text-amber-300">
            Browse Previous Studies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24"
    >
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Live Status Banner */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            {liveSession.isLive ? (
              <div className="inline-flex items-center gap-3 bg-red-600 px-6 py-3 rounded-full">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                <span className="font-bold text-lg">NOW LIVE</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-3 bg-amber-600 px-6 py-3 rounded-full">
                <Clock className="w-5 h-5" />
                <span className="font-bold">Starts in {timeUntilLive}</span>
              </div>
            )}
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{liveSession.title}</h1>
            <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto">{liveSession.description}</p>

            <div className="flex items-center justify-center gap-6 text-gray-400 mb-8">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span>{liveSession.speaker}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{new Date(liveSession.scheduledTime).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{new Date(liveSession.scheduledTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>
          </motion.div>

          {/* Video Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-12"
          >
            <div className="relative group max-w-3xl mx-auto">
              <div className="aspect-video rounded-2xl overflow-hidden bg-gray-800 shadow-2xl">
                <img
                  src={liveSession.thumbnail}
                  alt={liveSession.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/60 transition-colors">
                  <motion.a
                    href={liveSession.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 px-8 py-4 rounded-full text-white font-medium transition-colors ${liveSession.isLive
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-amber-600 hover:bg-amber-700'
                      }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Play className="w-6 h-6" />
                    <span>{liveSession.isLive ? 'Join Live Stream' : 'Set Reminder'}</span>
                    <ExternalLink className="w-5 h-5" />
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to={`/study?id=null`}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-lg text-white font-medium transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Study Notes & Q&A</span>
            </Link>

            <Link
              to="/library"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Browse Previous Studies →
            </Link>
          </motion.div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-16 text-center"
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 max-w-2xl mx-auto">
              <h3 className="text-xl font-bold mb-4">What to Expect</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Interactive Bible study with live Q&A</li>
                <li>• Practical application of biblical principles</li>
                <li>• Community discussion and prayer</li>
                <li>• Study notes available after the session</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}